import { ChevronDown } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

const CUSTOM = "custom";

// Matches the padding of the multi-select pills beside it, kept tight so the
// whole scope row still fits on one line once Watch adds the cadence pill.
const pillClass =
  "rounded-full border border-outline bg-surface px-2 py-1 text-xs text-text outline-none transition hover:bg-muted-surface";

type DaysSelectProps = {
  value: number;
  onChange: (days: number) => void;
  /** Offered in the list; the current value is folded in when it is not one. */
  presets: number[];
  /** How a day count reads as an option. */
  format: (days: number) => string;
  /** Trailing word while a custom number is being typed. */
  unitLabel: string;
  max?: number;
};

/**
 * A day count as one pill. Picking "Custom…" turns the pill into a number
 * input until a value is committed, and the pill then reads that value back —
 * so the two states are one control rather than a select plus a field.
 */
const DaysSelect = ({ value, onChange, presets, format, unitLabel, max }: DaysSelectProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);

  if (draft !== null) {
    const commit = () => {
      const days = Math.round(Number(draft));
      const valid = Number.isFinite(days) && days >= 1;
      onChange(valid ? Math.min(days, max ?? Number.POSITIVE_INFINITY) : value);
      setDraft(null);
    };

    return (
      <span className="flex items-center gap-1 rounded-full border border-outline bg-surface px-2 py-1 text-xs text-text">
        <input
          type="number"
          aria-label={unitLabel}
          min={1}
          max={max}
          autoFocus
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={event => {
            if (event.key === "Enter") {
              event.preventDefault();
              commit();
            }
          }}
          className="w-12 bg-transparent text-text outline-none"
        />
        <span className="text-text-dim">{unitLabel}</span>
      </span>
    );
  }

  const options = presets.includes(value) ? presets : [...presets, value].sort((a, b) => a - b);

  return (
    <span className="relative inline-flex">
      <select
        aria-label={unitLabel}
        value={String(value)}
        onChange={event => {
          const raw = event.target.value;
          if (raw === CUSTOM) setDraft(String(value));
          else onChange(Number(raw));
        }}
        // Native select arrows differ by browser and did not line up with the
        // two Lucide chevrons beside this pill. Hide it and draw the same icon
        // at the same size and inset instead.
        className={`${pillClass} appearance-none pr-7`}
      >
        {options.map(days => (
          <option key={days} value={days}>
            {format(days)}
          </option>
        ))}
        <option value={CUSTOM}>{t("custom_ellipsis")}</option>
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-text-dim"
        aria-hidden="true"
      />
    </span>
  );
};

export default DaysSelect;
